import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { publicParentApi } from "@/lib/parentApi";
import { useLanguage } from "@/context/LanguageContext";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

export default function ParentForgotPassword() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (window.turnstile && containerRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "auto",
          callback: (t) => setTurnstileToken(t),
          "error-callback": () => setTurnstileToken(""),
          "expired-callback": () => setTurnstileToken(""),
        });
      } else if (!window.turnstile) {
        setTimeout(tryRender, 200);
      }
    };
    tryRender();
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (e) {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error(t("parent.pleaseCaptcha"));
      return;
    }
    setLoading(true);
    try {
      await publicParentApi.post("/parent/forgot-password", {
        email,
        website,
        turnstile_token: turnstileToken,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-burns-black flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-32 sm:h-36 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-white">{t("parent.forgotTitle")}</h1>
        </div>

        {sent ? (
          <div className="text-center space-y-3">
            <Mail className="w-10 h-10 text-burns-gold mx-auto" />
            <p className="text-white">{t("parent.forgotSent")}</p>
            <p className="text-zinc-400 text-sm">{t("parent.forgotSentHint")}</p>
            <Link to="/parent/login" className="inline-block text-burns-gold hover:underline text-sm pt-3">{t("parent.backToLogin")}</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-zinc-400 text-sm">{t("parent.forgotIntro")}</p>

            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
              <label htmlFor="website-field">Website</label>
              <input id="website-field" type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">{t("parent.email")}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" />
            </div>

            {TURNSTILE_SITE_KEY && <div ref={containerRef} className="flex justify-center" />}

            <Button type="submit" disabled={loading || !email || (TURNSTILE_SITE_KEY && !turnstileToken)} className="w-full bg-burns-red hover:bg-burns-red/80 text-white">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t("parent.sendLink")}
            </Button>

            <div className="flex justify-between text-sm pt-2">
              <Link to="/parent/login" className="text-zinc-400 hover:text-white">{t("parent.backToLogin")}</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
